import React, { useState, useEffect } from 'react';
import { Upload, Camera, FileText, CreditCard, AlertCircle, X, Check } from 'lucide-react';
import { RegistrationForm } from '../../types';
import { APP_CONFIG, VALIDATION_RULES, ERROR_MESSAGES } from '../../utils/constants';

interface DocumentsStepProps {
  formData: RegistrationForm;
  updateFormData: (data: any) => void;
}

const DocumentsStep: React.FC<DocumentsStepProps> = ({ formData, updateFormData }) => {
  const [localData, setLocalData] = useState(formData.documents);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [photoPreview, setPhotoPreview] = useState<string[]>([]);

  useEffect(() => {
    updateFormData({ documents: localData });
  }, [localData, updateFormData]);

  useEffect(() => {
    // Create photo previews
    const previews: string[] = [];
    localData.photos.forEach((file) => {
      const url = URL.createObjectURL(file);
      previews.push(url);
    });
    setPhotoPreview(previews);

    // Cleanup function
    return () => {
      previews.forEach(url => URL.revokeObjectURL(url));
    };
  }, [localData.photos]);

  const validateFile = (file: File, type: 'photo' | 'document') => {
    if (file.size > APP_CONFIG.maxPhotoSize) {
      return ERROR_MESSAGES.fileTooLarge;
    }
    
    if (!APP_CONFIG.allowedImageTypes.includes(file.type)) {
      return ERROR_MESSAGES.invalidFileType;
    }
    
    return '';
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const currentPhotos = localData.photos.length;
    
    if (currentPhotos + files.length > APP_CONFIG.maxPhotos) {
      setErrors(prev => ({ ...prev, photos: ERROR_MESSAGES.maxPhotos }));
      return;
    }

    const validFiles: File[] = [];
    const fileErrors: string[] = [];

    files.forEach(file => {
      const error = validateFile(file, 'photo');
      if (error) {
        fileErrors.push(`${file.name}: ${error}`);
      } else {
        validFiles.push(file);
      }
    });

    if (fileErrors.length > 0) {
      setErrors(prev => ({ ...prev, photos: fileErrors.join(', ') }));
    } else {
      setErrors(prev => ({ ...prev, photos: '' }));
      setLocalData(prev => ({
        ...prev,
        photos: [...prev.photos, ...validFiles]
      }));
    }
  };

  const removePhoto = (index: number) => {
    setLocalData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
    setErrors(prev => ({ ...prev, photos: '' }));
  };

  const handleDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>, docType: 'aadhaar' | 'pan') => {
    const file = e.target.files?.[0];
    if (!file) return;

    const error = validateFile(file, 'document');
    if (error) {
      setErrors(prev => ({ ...prev, [`${docType}File`]: error }));
      return;
    }

    setErrors(prev => ({ ...prev, [`${docType}File`]: '' }));
    setLocalData(prev => ({
      ...prev,
      [`${docType}File`]: file
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setLocalData(prev => ({ ...prev, [name]: checked }));
    } else {
      setLocalData(prev => ({ ...prev, [name]: value }));
      
      // Validate Aadhaar and PAN
      if (name === 'aadhaarNumber') {
        const error = value && !VALIDATION_RULES.aadhaar.test(value) ? ERROR_MESSAGES.invalidAadhaar : '';
        setErrors(prev => ({ ...prev, aadhaarNumber: error }));
      }
      
      if (name === 'panNumber') {
        const error = value && !VALIDATION_RULES.pan.test(value.toUpperCase()) ? ERROR_MESSAGES.invalidPan : '';
        setErrors(prev => ({ ...prev, panNumber: error }));
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Photos Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
          <Camera className="w-5 h-5 text-primary-600" />
          <span>Profile Photos</span>
          <span className="text-sm text-gray-500 font-normal">(Optional, max 4 photos)</span>
        </h3>
        
        <div className="space-y-4">
          {/* Photo Upload */}
          <div>
            <label className="block w-full">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-500 cursor-pointer transition-colors">
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-1">
                  Click to upload photos or drag and drop
                </p>
                <p className="text-xs text-gray-500">
                  JPG, PNG up to 5MB each • Maximum 4 photos
                </p>
              </div>
              <input
                type="file"
                multiple
                accept={APP_CONFIG.allowedImageTypes.join(',')}
                onChange={handlePhotoUpload}
                className="hidden"
                disabled={localData.photos.length >= APP_CONFIG.maxPhotos}
              />
            </label>
          </div>

          {/* Photo Preview */}
          {photoPreview.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {photoPreview.map((preview, index) => (
                <div key={index} className="relative group">
                  <img
                    src={preview}
                    alt={`Photo ${index + 1}`}
                    className="w-full h-24 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {errors.photos && (
            <p className="text-sm text-red-600 flex items-center space-x-1">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.photos}</span>
            </p>
          )}
        </div>
      </div>

      {/* Aadhaar Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
          <FileText className="w-5 h-5 text-primary-600" />
          <span>Aadhaar Details</span>
          <span className="text-sm text-red-500">*</span>
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="aadhaarNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Aadhaar Number *
            </label>
            <input
              type="text"
              id="aadhaarNumber"
              name="aadhaarNumber"
              value={localData.aadhaarNumber}
              onChange={handleChange}
              className={`block w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                errors.aadhaarNumber ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter 12-digit Aadhaar number"
              maxLength={12}
              required
            />
            {errors.aadhaarNumber && (
              <p className="mt-1 text-sm text-red-600">{errors.aadhaarNumber}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Aadhaar Document (Optional)
            </label>
            <label className="block">
              <div className="border border-gray-300 rounded-lg p-3 text-center cursor-pointer hover:bg-gray-50 transition-colors">
                {localData.aadhaarFile ? (
                  <div className="flex items-center justify-center space-x-2 text-green-600">
                    <Check className="w-4 h-4" />
                    <span className="text-sm">File uploaded</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2 text-gray-600">
                    <Upload className="w-4 h-4" />
                    <span className="text-sm">Upload Aadhaar</span>
                  </div>
                )}
              </div>
              <input
                type="file"
                accept={APP_CONFIG.allowedImageTypes.join(',')}
                onChange={(e) => handleDocumentUpload(e, 'aadhaar')}
                className="hidden"
              />
            </label>
            {errors.aadhaarFile && (
              <p className="mt-1 text-sm text-red-600">{errors.aadhaarFile}</p>
            )}
          </div>
        </div>
      </div>

      {/* PAN Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center space-x-2">
          <CreditCard className="w-5 h-5 text-primary-600" />
          <span>PAN Details</span>
          <span className="text-sm text-gray-500 font-normal">(Optional)</span>
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="panNumber" className="block text-sm font-medium text-gray-700 mb-1">
              PAN Number
            </label>
            <input
              type="text"
              id="panNumber"
              name="panNumber"
              value={localData.panNumber}
              onChange={handleChange}
              className={`block w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                errors.panNumber ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Enter PAN number (e.g., ABCDE1234F)"
              maxLength={10}
              style={{ textTransform: 'uppercase' }}
            />
            {errors.panNumber && (
              <p className="mt-1 text-sm text-red-600">{errors.panNumber}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              PAN Document
            </label>
            <label className="block">
              <div className="border border-gray-300 rounded-lg p-3 text-center cursor-pointer hover:bg-gray-50 transition-colors">
                {localData.panFile ? (
                  <div className="flex items-center justify-center space-x-2 text-green-600">
                    <Check className="w-4 h-4" />
                    <span className="text-sm">File uploaded</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2 text-gray-600">
                    <Upload className="w-4 h-4" />
                    <span className="text-sm">Upload PAN</span>
                  </div>
                )}
              </div>
              <input
                type="file"
                accept={APP_CONFIG.allowedImageTypes.join(',')}
                onChange={(e) => handleDocumentUpload(e, 'pan')}
                className="hidden"
              />
            </label>
            {errors.panFile && (
              <p className="mt-1 text-sm text-red-600">{errors.panFile}</p>
            )}
          </div>
        </div>
      </div>

      {/* Terms and Conditions */}
      <div className="space-y-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="font-medium text-yellow-900 mb-2">📋 Important Notes</h4>
          <ul className="text-sm text-yellow-800 space-y-1">
            <li>• Your profile will be manually verified by our team</li>
            <li>• All documents are kept secure and confidential</li>
            <li>• Verification may take 24-48 hours</li>
            <li>• You can browse profiles only after verification</li>
          </ul>
        </div>

        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            id="agreeTerms"
            name="agreeTerms"
            checked={localData.agreeTerms}
            onChange={handleChange}
            className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            required
          />
          <label htmlFor="agreeTerms" className="text-sm text-gray-700">
            I agree to the{' '}
            <a href="/terms" className="text-primary-600 hover:text-primary-500 underline">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy" className="text-primary-600 hover:text-primary-500 underline">
              Privacy Policy
            </a>
            . I confirm that all information provided is accurate and truthful.
          </label>
        </div>
      </div>
    </div>
  );
};

export default DocumentsStep;